import React, { useState, useEffect, useRef } from "react";

import Page from "pages/Page";
import { LogoutHook } from "services/Api";
import { useNotificationManager } from "notifications/NotificationManager";
import { useCircuitBreakerStatus } from "circuitbreaker/CircuitBreaker";

const ServiceUnavailable = ({ componentName = "[ServiceUnavailable]" }) => {
    const [submitButtonText, _setSubmitButtonText] = useState("Reconnect Now");
    const [previousRoute, setPreviousRoute] = useState<string>("");
    const [isNavigating, setIsNavigating] = useState(false);
    const [manualReconnectRequested, setManualReconnectRequested] = useState(false);

    const notificationManager = useNotificationManager();
    const circuitBreakerStatus = useCircuitBreakerStatus();
    const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Remember the previous navigation route
    useEffect(() => {
        const currentRoute = window.location.pathname + window.location.search;
        const storedRoute = sessionStorage.getItem("previousRoute") || "/";

        // Store current route as previous for next time, but use stored route for this instance
        sessionStorage.setItem("previousRoute", currentRoute);
        setPreviousRoute(storedRoute);
    }, []);

    // Auto-reload when circuit breaker becomes healthy again
    useEffect(() => {
        if (circuitBreakerStatus.isHealthy && !isNavigating) {
            console.log("[ServiceUnavailable] Circuit breaker is healthy - auto-reloading previous route");

            setIsNavigating(true);
            notificationManager.clearAllNotifications();

            // Ensure we don't reload back to the service unavailable page
            const targetRoute = previousRoute === "/serviceunavailable" ? "/" : previousRoute;

            // Add a small delay to ensure the circuit breaker state has fully stabilized
            navigationTimeoutRef.current = setTimeout(() => {
                console.log(`[ServiceUnavailable] Navigating to: ${targetRoute}`);
                try {
                    if (targetRoute && targetRoute !== "/serviceunavailable") {
                        window.location.href = targetRoute;
                    }
                    else {
                        window.location.href = "/";
                    }
                }
                catch (error) {
                    console.error("[ServiceUnavailable] Navigation error:", error);
                    // Reset navigation state if navigation fails
                    setIsNavigating(false);
                }
            }, 1000); // Increased delay to 1 second
        }
    }, [circuitBreakerStatus.isHealthy, previousRoute, notificationManager, isNavigating]);

    // Reset navigation state if circuit breaker becomes unhealthy again
    useEffect(() => {
        if (!circuitBreakerStatus.isHealthy && isNavigating) {
            console.log("[ServiceUnavailable] Circuit breaker became unhealthy, resetting navigation state");
            setIsNavigating(false);
            setManualReconnectRequested(false);
            if (navigationTimeoutRef.current) {
                clearTimeout(navigationTimeoutRef.current);
                navigationTimeoutRef.current = null;
            }
        }
    }, [circuitBreakerStatus.isHealthy, isNavigating]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (navigationTimeoutRef.current) {
                clearTimeout(navigationTimeoutRef.current);
            }
        };
    }, []);

    // ---=== Utilities ===--- //

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Prevent multiple manual reloads
        if (isNavigating || manualReconnectRequested) {
            return;
        }

        console.log("[ServiceUnavailable] Manual reconnect requested");
        setManualReconnectRequested(true);
        notificationManager.clearAllNotifications();

        // Reload the previous navigation route, ensuring we don't route back to ourselves
        const targetRoute = previousRoute === "/serviceunavailable" ? "/" : previousRoute;

        // Add a delay before attempting navigation to show the reconnecting state
        setTimeout(() => {
            try {
                if (targetRoute) {
                    console.log(`[ServiceUnavailable] Manual navigation to: ${targetRoute}`);
                    window.location.href = targetRoute;
                }
                else {
                    console.log("[ServiceUnavailable] Manual page reload");
                    window.location.reload();
                }
            }
            catch (error) {
                console.error("[ServiceUnavailable] Manual navigation error:", error);
                // Reset states if navigation fails
                setIsNavigating(false);
                setManualReconnectRequested(false);
            }
        }, 1000);
    };

    const handleSubmitButton = async (e) => {
        void handleSubmit(e);
    };

    const handleKeyPress = async (e) => {
        if (e.key === "Enter") {
            void handleSubmit(e);
        }
    };

    const logoutHook: LogoutHook = async(_componentName, _logoutReason, _onLogoutHookAccept, onLogoutHookReject) => {
        // Prevent the base Page.handleLogout() from performing a logout.
        // This allows the ServiceUnavailable page to render its own UI without automatically
        // displaying the Login page.
        onLogoutHookReject();
    };

    // Get display text based on current state
    const getMainDisplayText = () => {
        if (isNavigating) {
            return "KarunaCLOUD available!";
        }
        return "KarunaCLOUD not available!";
    };

    const getStatusComponent = () => {
        if (isNavigating) {
            return (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 w-80 mx-auto">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="animate-spin h-4 w-4 text-yellow-400" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <div className="ml-3 flex-1">
                            <div className="text-sm text-yellow-700">
                                Reconnecting...
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Show simple text for healthy state
        if (circuitBreakerStatus.isHealthy) {
            return (
                <div className="self-stretch text-center text-gray-500 text-base font-normal leading-normal">
                    Healthy
                </div>
            );
        }

        // Show spinner component for recovering/reconnecting states OR when manual reconnect is requested
        if (circuitBreakerStatus.isRecovering || circuitBreakerStatus.isOpen || manualReconnectRequested) {
            const seconds = circuitBreakerStatus.timeUntilRetry ? Math.ceil(circuitBreakerStatus.timeUntilRetry / 1000) : 0;

            return (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 w-80 mx-auto">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="animate-spin h-4 w-4 text-yellow-400" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <div className="ml-3 flex-1">
                            <div className="text-sm text-yellow-700">
                                {
                                    manualReconnectRequested
                                        ? (
                                            <>Reconnecting...</>
                                        )
                                        : circuitBreakerStatus.isRecovering && seconds > 0
                                            ? (
                                                <>Attempting Reconnection in {seconds}s...</>
                                            )
                                            : (
                                                <>Reconnecting...</>
                                            )
                                }
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Fallback for any other states
        return (
            <div className="self-stretch text-center text-gray-500 text-base font-normal leading-normal">
                {circuitBreakerStatus.getStatusText()}
            </div>
        );
    };

    const getButtonText = () => {
        if (isNavigating) {
            return "Connecting...";
        }
        if (manualReconnectRequested) {
            return "Reconnecting...";
        }
        // When automatic reconnect is actively being attempted (timer reached 0 and trying to reconnect)
        if (circuitBreakerStatus.canRetryNow) {
            return "Reconnecting...";
        }
        return submitButtonText;
    };

    const isButtonDisabled = () => {
        // Disable button when:
        // 1. Actually navigating away (isNavigating = true)
        // 2. Manual reconnect was requested and is in progress (manualReconnectRequested = true)
        // 3. Automatic reconnect is actively being attempted (canRetryNow = true, meaning timer reached 0 and system is trying to reconnect)
        return isNavigating || manualReconnectRequested || circuitBreakerStatus.canRetryNow;
    };

    // ---=== Render ===--- //

    return (
        <React.Fragment>
            <Page
                componentName={componentName}
                internal={true}
                // logoutHook={logoutHook}
            >
                <div className="h-screen flex items-center justify-center pb-12 ">
                    <div className=" w-96 h-96 flex-col justify-center content-center items-center gap-4 inline-flex">
                        <div className="self-stretch h-36 flex-col justify-start items-center gap-6 flex" id="title">
                            <div className="justify-start items-start inline-flex">
                                <img className="w-12 h-12" src="/icons/icon.png" alt="Karuna Logo"/>
                            </div>
                            <div className="self-stretch h-20 flex-col justify-start items-start gap-3 flex lg:h-40">
                                <div className="self-stretch text-center text-gray-900 text-3xl font-semibold leading-9">Provider Portal</div>
                                <div className="self-stretch text-center text-gray-600 text-2xl font-normal leading-normal">
                                    {getMainDisplayText()}
                                    <br/>
                                </div>
                            </div>
                        </div>
                        <div className="w-full flex justify-center my-4">
                            {getStatusComponent()}
                        </div>
                        <form className="w-full max-w-sm" onSubmit={handleSubmitButton} onKeyUp={(e) => handleKeyPress(e)}>
                            <div className="self-stretch h-11 flex-col justify-start items-start gap-4 flex">
                                <button
                                    type="submit"
                                    disabled={isButtonDisabled()}
                                    className={`self-stretch px-4 py-2.5 ${isButtonDisabled() ? "bg-gray-400" : "bg-brand-600 hover:bg-brand-700"} rounded-lg shadow justify-center items-center gap-1.5 inline-flex ${isButtonDisabled() ? "cursor-not-allowed" : "cursor-pointer"}`}
                                >
                                    <div className="px-0.5 justify-center items-center flex">
                                        <div className="text-white text-base font-semibold leading-normal">
                                            {getButtonText()}
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </Page>
        </React.Fragment>
    );
};

export default ServiceUnavailable;
